import { useEffect, useState } from 'react';
import { publicCreatorPriceOptions } from '../api/creatorPrices';

/**
 * Phase 52 · Fetch active creator price options for a given scope.
 *
 * Returns `{ options, credits, loading }`:
 *  - `options` — full server objects (credits, label, eur_estimate, ...)
 *  - `credits` — sorted list of integers, ready for a <Select>
 *  - `loading` — true until the first fetch completes
 *
 * Falls back to the frontend hardcoded list on network failure so
 * the create flow is never blocked.
 */
const FALLBACK = [50, 100, 150, 200, 250, 300, 500, 750, 990];

// Module-level cache to avoid re-fetching on every mount within the
// same session. Keyed by scope.
const _cache = new Map();
const _inflight = new Map();

export default function useCreatorPriceOptions(scope) {
  const key = scope || 'all';
  const cached = _cache.get(key);
  const [options, setOptions] = useState(cached?.options || []);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let alive = true;
    if (_cache.has(key)) {
      setOptions(_cache.get(key).options);
      setLoading(false);
      return () => {};
    }
    const doFetch = async () => {
      if (_inflight.has(key)) {
        try { await _inflight.get(key); } catch { /* fall through */ }
        if (!alive) return;
        setOptions(_cache.get(key)?.options || []);
        setLoading(false);
        return;
      }
      const p = publicCreatorPriceOptions(scope);
      _inflight.set(key, p);
      try {
        const rows = await p;
        _cache.set(key, { options: rows });
        if (alive) {
          setOptions(rows);
          setLoading(false);
        }
      } catch {
        if (alive) {
          setOptions([]);
          setLoading(false);
        }
      } finally {
        _inflight.delete(key);
      }
    };
    doFetch();
    return () => { alive = false; };
  }, [key, scope]);

  const credits = options.length
    ? Array.from(new Set(options.map((o) => Number(o.credits)))).sort((a, b) => a - b)
    : FALLBACK;

  return { options, credits, loading };
}
